<template>
  <table class="w-full text-[13px]" style="border-collapse: collapse; table-layout: fixed">
    <!-- Percentage widths summing to 100, per app_ui/tables: the table always
         fills its container and every column shares growth proportionally.
         Pixel widths here would create a hard minimum and force a scrollbar.
         Support_Table solves density the other way — pixel widths with
         drag-resize persisted through BrowserStore — and the two tables have
         genuinely different constraints; picking between them is its own
         decision, not a side effect of adding a column.

         table-layout: fixed IS WHAT MAKES THE ABOVE TRUE. Under the default
         `auto` layout the colgroup percentages are advisory: the browser
         computes each column's minimum from its CONTENT and lets the table
         exceed its container, which is the scrollbar the percentages were
         chosen to avoid. That was never noticed at ten columns because the
         content floor stayed under the available width. Measured at 1440px with
         the detail panel open, eleven columns had a 929px floor in a 750px
         container — a 179px horizontal scroll, and 339px at 1280px. Under
         `fixed` the percentages are authoritative, the table is exactly as wide
         as its container at every width, and overflow becomes row height
         instead. Verified 1280/1440/1680/1920, panel open and closed.

         `fixed` only holds if nothing inside a cell refuses to shrink, so the
         two chips truncate, the prose columns carry `break-words`, and the date
         cells lost `whitespace-nowrap` — a nowrap cell in a fixed column is a
         guarantee of overflow at exactly the width where it matters. ID and Pri
         deliberately have neither: they are short atomic values, and a
         table-wide `break-words` split "1365" across two lines.

         RE-BUDGETED WHEN Type AND Due LANDED, not appended to. Two columns
         added on top of ten would have pushed the sum past 100 and let the
         browser silently re-apportion the excess. Created by was dropped to pay
         for them and RESTORED 2026-09-13 (Nick): with Cerberus filing a fifth
         of the board and 91 of its drafts untriaged, who filed a row has to be
         readable while scanning, not only filterable. It takes 7%, paid for by
         Requirement 22 → 19, Queue 12 → 10, Delivery 10 → 9, Tenant 9 → 8.

         RE-BUDGETED AGAIN for the checkbox column, the same way: the three
         widest columns each gave up their share (Requirement 24 → 22, Queue
         13 → 12, Owner 10 → 9) rather than a twelfth `col` being appended and
         the sum drifting to 104. The checkbox takes 4% and the status dot drops
         its leading `pl-4` to `px-2`, because it is no longer the first column
         — `pl-4` in a 3% column is 16px of padding in a 22px share, which
         app_ui/tables measured overrunning on its own at 1280px. -->
    <colgroup>
      <col style="width: 4%" />
      <col style="width: 3%" />
      <col style="width: 5%" />
      <col style="width: 4%" />
      <col style="width: 9%" />
      <col style="width: 19%" />
      <col style="width: 10%" />
      <col style="width: 9%" />
      <col style="width: 8%" />
      <col style="width: 9%" />
      <col style="width: 7%" />
      <col style="width: 7%" />
      <col style="width: 6%" />
    </colgroup>
    <thead>
      <tr class="bg-gray-50 border-b border-gray-200 sticky top-0 z-[1]">
        <!-- SELECT ALL *VISIBLE*, never the whole payload. `tickets` is what the
             current filters admit, which is the only set a person can see and
             therefore the only set they can mean. Indeterminate when a subset
             is checked, so the header states the difference between "some of
             these" and "all of these" rather than reading as unchecked. -->
        <th class="py-2.5 pl-3 pr-1">
          <input
            type="checkbox"
            class="w-3.5 h-3.5 accent-primary-500 align-middle"
            :checked="allChecked"
            :indeterminate.prop="someChecked && !allChecked"
            :aria-label="allChecked ? 'Deselect all visible' : 'Select all visible'"
            :title="allChecked ? 'Deselect all visible' : 'Select all visible'"
            @change="$emit('toggle-all', !allChecked)"
          />
        </th>
        <Hades_SortHeader
          sort-key="investigated" :active-key="sortKey" :direction="sortDir"
          th-class="px-2 py-2.5" @sort="$emit('sort', $event)"
        >&nbsp;</Hades_SortHeader>
        <Hades_SortHeader
          v-for="col in columns"
          :key="col.key"
          :sort-key="col.key"
          :active-key="sortKey"
          :direction="sortDir"
          :th-class="col.last ? TH_LAST : TH"
          @sort="$emit('sort', $event)"
        >{{ col.label }}</Hades_SortHeader>
      </tr>
    </thead>
    <tbody>
      <tr v-if="!tickets.length">
        <td colspan="13" class="px-4 py-6 text-gray-600">
          No requirements match. Try clearing the search, a filter, or the
          customer and queue selection.
        </td>
      </tr>
      <tr
        v-for="ticket in tickets"
        :key="ticket.requirement"
        class="border-b border-gray-100 cursor-pointer"
        :class="ticket.requirement === selectedRequirement ? 'bg-gray-50' : 'hover:bg-gray-50/60'"
        @click="$emit('select', ticket)"
      >
        <!-- @click.stop, or checking a row would also open the verdict panel
             over the table you are picking rows out of. The label stretches the
             cell so the click target is the column, not a 14px box. -->
        <td class="py-2.5 pl-3 pr-1 align-top" @click.stop>
          <label class="flex items-center cursor-pointer">
            <input
              type="checkbox"
              class="w-3.5 h-3.5 accent-primary-500"
              :checked="checked.has(ticket.requirement)"
              :aria-label="`Select requirement ${ticket.requirement}`"
              @change="$emit('toggle-check', ticket.requirement)"
            />
          </label>
        </td>
        <td class="px-2 py-2.5 align-top">
          <span
            class="inline-block w-2 h-2 rounded-full mt-1"
            :class="ticket.investigated ? 'bg-gray-800' : 'border border-gray-100'"
            :title="ticket.investigated ? 'Investigated' : 'Not yet investigated'"
          />
        </td>
        <td class="px-2 py-2.5 align-top text-gray-600 font-mono text-xs">
          {{ ticket.requirement }}
        </td>
        <td
          class="px-2 py-2.5 align-top"
          :class="isCritical(ticket.requirement_priority)
            ? 'font-semibold text-red-dark' : 'text-gray-800'"
        >
          {{ priorityLabel(ticket.requirement_priority) }}
        </td>
        <!-- TYPE IS A COLUMN NOW. It was 11px grey subtext under the title,
             sharing a line with the status, which is why it read as absent. -->
        <td class="px-2 py-2.5 align-top">
          <!-- TRUNCATED, NOT WRAPPED. A chip that wraps stops reading as a chip:
               "Support Ticket" rendered as a two-line coloured block and
               "Bluestone Physician Services" as a three-line one, which also set
               the table's minimum width and forced a horizontal scrollbar
               whenever the detail panel was open below 1680px. `truncate` takes
               the element out of min-content entirely, so the column can give
               its space back to the title. The full value stays in the title
               attribute and in the panel. -->
          <span
            v-if="ticket.requirement_type"
            class="inline-block align-top max-w-full truncate px-1.5 py-0.5 rounded text-[11px] font-medium leading-tight"
            :class="typeClass(ticket.requirement_type)"
            :title="ticket.requirement_type"
          >{{ ticket.requirement_type }}</span>
          <span v-else class="text-gray-400">—</span>
        </td>
        <td class="px-2 py-2.5 align-top text-gray-900 break-words">
          <!-- Free text that can name a customer or patient: redacted in
               Demo Mode, never token-substituted. The status line below is an
               enum and stays readable. -->
          <div class="sensitive font-medium leading-snug">{{ ticket.requirement_title }}</div>
          <div class="text-[11px] text-gray-600 mt-0.5">
            {{ statusLabel(ticket.requirement_status) }}
          </div>
        </td>
        <!-- QUEUE PLUS ATTENTION. The `active` queue is four attention states
             collapsed into one bucket by cerberus/hades/config.yaml, so "Active"
             alone cannot tell in-progress from owned-and-idle. The distinction
             was always in the data; it had simply never been rendered.
             Un-investigated rows print no subline at all — the cell already
             says "Not investigated", and an "Unknown" under it would read as a
             judgment that was never made. -->
        <td
          class="px-2 py-2.5 align-top break-words"
          :class="ticket.investigated ? 'text-gray-800' : 'text-gray-400 italic'"
        >
          {{ ticket.investigated ? queueLabels(ticket) : 'Not investigated' }}
          <div
            v-if="ticket.investigated && ticket.attention"
            class="text-[11px] text-gray-600 mt-0.5 leading-snug"
          >{{ attentionLabel(ticket.attention) }}</div>
        </td>
        <td class="px-2 py-2.5 align-top text-gray-600 break-words">
          {{ ticket.investigated ? deliveryLabel(ticket.delivery) : '' }}
        </td>
        <!-- tenantColorFor() derives the colour from the name, so a customer
             looks the same here as on every other Support surface and on the
             pills above the board. -->
        <td class="px-2 py-2.5 align-top">
          <span
            v-if="ticket.tenant_name"
            class="sensitive inline-block align-top max-w-full truncate px-1.5 py-0.5 rounded text-[11px] font-medium leading-tight"
            :style="tenantColorFor(ticket.tenant_name)"
            :title="ticket.tenant_name"
          >{{ ticket.tenant_name }}</span>
          <span v-else class="text-gray-400">—</span>
        </td>
        <!-- UNOWNED IS THE INTERESTING STATE on a board about neglected work,
             so it is called out rather than left as a neutral em-dash. 81 of
             404 rows are unowned. -->
        <td class="px-2 py-2.5 align-top break-words">
          <span v-if="ticket.owner_name" class="text-gray-800">{{ ticket.owner_name }}</span>
          <span v-else class="text-gray-400 italic">Unowned</span>
        </td>
        <!-- Same buckets as the Created by filter: the backend collapses rows
             with no requester and no cerberus source onto 'Unattributed'. -->
        <td class="px-2 py-2.5 align-top break-words">
          <span
            v-if="ticket.creator_name"
            class="text-gray-800"
            :class="{ sensitive: isPersonCreator(ticket.creator_name) }"
          >{{ ticket.creator_name }}</span>
          <span v-else class="text-gray-400 italic">Unattributed</span>
        </td>
        <!-- DATE PLUS AGE. The date alone does not answer "how long has this sat
             there", and an age alone loses the ability to scan for a cluster of
             filings on one day. Age is emphasised over 90 days, which is the
             threshold at which a requirement is stale rather than merely open. -->
        <td class="px-2 py-2.5 align-top">
          <template v-if="ticket.requirement_created_at">
            <div class="text-gray-600">
              {{ shortDate(ticket.requirement_created_at) }}
            </div>
            <div
              class="text-[11px] mt-0.5"
              :class="isStale(ticket.requirement_created_at)
                ? 'text-red-dark font-medium' : 'text-gray-400'"
            >
              {{ ageLabel(ticket.requirement_created_at) }}
            </div>
            <div
              v-if="ticket.customer_submitted"
              class="text-[11px] mt-0.5"
              :class="replyOverdue(ticket)
                ? 'text-red-dark font-medium' : 'text-gray-400'"
              :title="replyOverdue(ticket)
                ? 'Client-filed; next Kairon reply is past the priority window'
                : 'Client-filed; within the priority window'"
            >
              {{ replyLabel(ticket) }}
            </div>
          </template>
          <span v-else class="text-gray-400">—</span>
        </td>
        <!-- DUE, emphasised when overdue the way 90-day staleness already is on
             Created. "How long has this sat there" and "when is it owed" are
             different questions and the blitz asked both. -->
        <td class="pl-2 pr-4 py-2.5 align-top">
          <template v-if="ticket.requirement_due_date">
            <div
              :class="isOverdue(ticket.requirement_due_date)
                ? 'text-red-dark font-medium' : 'text-gray-600'"
            >
              {{ dueDate(ticket.requirement_due_date) }}
            </div>
            <div
              class="text-[11px] mt-0.5"
              :class="isOverdue(ticket.requirement_due_date)
                ? 'text-red-dark font-medium' : 'text-gray-400'"
            >
              {{ dueLabel(ticket.requirement_due_date) }}
            </div>
          </template>
          <span v-else class="text-gray-400">—</span>
        </td>
      </tr>
    </tbody>
  </table>
</template>

<script>
import Hades_SortHeader from './Hades_SortHeader.vue';
import {
  STATUS_LABELS, TYPE_CLASSES, daysAgoShortLabel, daysSince, daysUntil,
  formatDateValue, priorityShortLabel, priorityToCode, tenantColorFor,
} from '../support_constants';
import {
  ATTENTION_LABELS, DELIVERY_LABELS, QUEUE_LABELS, label,
} from './hades_constants';
import { isPersonCreator } from './hades_filters';

// Uppercase micro-label header, per app_ui/tables. Shared so the headers
// cannot drift from each other.
const TH = 'px-2 py-2.5 text-left text-[11px] font-semibold tracking-wide '
  + 'uppercase text-gray-600';

// Column order and labels in ONE list, so the headers cannot fall out of step
// with the sort keys. `key` is what the comparator sorts on.
const COLUMNS = [
  { key: 'requirement', label: 'ID' },
  { key: 'requirement_priority', label: 'Pri' },
  { key: 'requirement_type', label: 'Type' },
  { key: 'requirement_title', label: 'Requirement' },
  { key: 'queues', label: 'Queue' },
  { key: 'delivery', label: 'Delivery' },
  { key: 'tenant_name', label: 'Tenant' },
  { key: 'owner_name', label: 'Owner' },
  { key: 'creator_name', label: 'Created by' },
  { key: 'requirement_created_at', label: 'Created' },
  { key: 'requirement_due_date', label: 'Due', last: true },
];

export default {
  name: 'HadesTicketTable',
  components: { Hades_SortHeader },
  props: {
    tickets: { type: Array, required: true },
    // KEYED ON requirement, NOT id. An un-investigated row has id === null
    // (there is no HadesTicket yet), so every one of them collided on a null
    // key and selecting any of them highlighted all of them.
    selectedRequirement: { type: Number, default: null },
    // SORT STATE LIVES IN THE PARENT, not here. The parent owns the row array
    // and applies the comparator; this component only renders headers and emits
    // which one was clicked. Two sources of truth for "how is this sorted"
    // is how a table ends up showing one order and claiming another.
    sortKey: { type: String, default: null },
    sortDir: { type: String, default: 'asc' },
    // THE CHECKED SET IS KEYED ON `requirement`, LIKE selectedRequirement and
    // for the same reason: an un-investigated row has no HadesTicket, so its
    // `id` is null and every one of them would collide on that one key — the
    // row-selection bug fixed 2026-08-31, which a checkbox set would have
    // reintroduced silently as "checking one row checked 184".
    checked: { type: Set, default: () => new Set() },
  },
  emits: ['select', 'sort', 'toggle-check', 'toggle-all'],
  data() {
    return { TH, TH_LAST: `${TH} pr-4`, columns: COLUMNS };
  },
  computed: {
    // Over the RENDERED rows, so the header agrees with what is on screen. A
    // selection carried in from a wider view cannot make this read "all".
    checkedVisible() {
      return this.tickets.filter((t) => this.checked.has(t.requirement)).length;
    },
    allChecked() {
      return !!this.tickets.length && this.checkedVisible === this.tickets.length;
    },
    someChecked() {
      return this.checkedVisible > 0;
    },
  },
  methods: {
    queueLabels(ticket) {
      return (ticket.queues || []).map((q) => label(QUEUE_LABELS, q)).join(', ');
    },
    deliveryLabel(delivery) {
      return label(DELIVERY_LABELS, delivery);
    },
    priorityLabel(priority) {
      return priorityShortLabel(priority);
    },
    // Through the canonical fold, not a bare === 'p0'. 76 rows still hold the
    // pre-0664 string 'urgent', and those are the same severity — they were
    // rendering "P0" from the label map while missing the red emphasis.
    isCritical(priority) {
      return priorityToCode(priority) === 'p0';
    },
    statusLabel(status) {
      return label(STATUS_LABELS, status);
    },
    attentionLabel(attention) {
      return label(ATTENTION_LABELS, attention);
    },
    tenantColorFor,
    isPersonCreator,
    typeClass(typeName) {
      return TYPE_CLASSES[typeName] || 'bg-gray-100 text-gray-600';
    },
    // The d/mo fold is Hades's own — deliberately NOT utils/date.js
    // formatRelativeTime, which returns "about 5 months ago": three words too
    // long for a table column, and it wraps the cell. These rows span Mar to
    // Aug, so months are the working unit and d/mo keeps the column scannable
    // down its length. That reasoning was sound; support_constants.js was the
    // right place to take the day maths FROM, and was missed.
    //
    // daysSince() counts whole LOCAL calendar days and returns null for a value
    // that is missing or unparseable, which is the guard the hand-rolled
    // version needed two comments to explain: `new Date(null)` is epoch 0, a
    // perfectly valid date, so a NaN check alone rendered "Dec 31, 69 / 689mo"
    // flagged stale.
    ageLabel(value) {
      const days = daysSince(value);
      if (days === null || days < 0) return '';
      if (days < 1) return 'today';
      if (days < 60) return daysAgoShortLabel(value);
      return `${Math.floor(days / 30)}mo`;
    },
    isStale(value) {
      const days = daysSince(value);
      return days !== null && days >= 90;
    },
    replyOverdue(ticket) {
      return !!ticket.awaiting_reply || Date.parse(ticket.response_due_at) < Date.now();
    },
    // "How long since we last spoke to them", the number the customer counts.
    // Since our last reply when there is one; since they filed when there is
    // none. A customer comment since our last reply outranks both: that is
    // Brea's #4819 — "we commented in the ticket and nobody answered".
    replyLabel(ticket) {
      if (ticket.awaiting_reply) {
        const days = daysSince(ticket.last_customer_comment_at);
        return days < 1 ? 'customer replied · unanswered' : `customer replied ${days}d ago · unanswered`;
      }
      if (ticket.last_response_at) {
        const days = daysSince(ticket.last_response_at);
        return days < 1 ? 'replied today' : `replied ${days}d ago`;
      }
      const days = daysSince(ticket.requirement_created_at);
      return days < 1 ? 'no reply' : `no reply · ${days}d`;
    },
    shortDate(iso) {
      if (!iso) return '';
      // STILL RENDERED IN UTC, matching how the timestamp is stored. Left to
      // local time, a requirement filed at 03:21 UTC on Mar 2 displays as
      // "Mar 1" to anyone west of Greenwich — the date shown would not be the
      // date in the database, and two people would disagree about when a ticket
      // was filed. formatDateValue passes opts straight through, so the shared
      // helper carries the decision rather than replacing it.
      //
      // The Due column does NOT do this: requirement_due_date is a bare
      // calendar day, and toLocalDate() deliberately applies no conversion at
      // all to one. See dueDate() below.
      const date = new Date(iso);
      if (Number.isNaN(date.getTime())) return '';
      const opts = { month: 'short', day: 'numeric', timeZone: 'UTC' };
      if (date.getUTCFullYear() !== new Date().getUTCFullYear()) opts.year = '2-digit';
      return formatDateValue(iso, opts);
    },
    // NO timeZone OPTION, and that is the whole point. requirement_due_date is a
    // Django DateField, so it arrives as a bare 'YYYY-MM-DD' — Sep 3 is Sep 3
    // everywhere, and toLocalDate() applies no conversion to one. Passing it
    // through `new Date()` would parse it as UTC midnight and render the
    // previous day for every viewer west of Greenwich, which is the other half
    // of the bug isOverdue() guards against.
    dueDate(value) {
      const opts = { month: 'short', day: 'numeric' };
      const days = daysUntil(value);
      // Beyond a year out or a year late the bare day is ambiguous.
      if (days !== null && Math.abs(days) > 300) opts.year = '2-digit';
      return formatDateValue(value, opts);
    },
    // daysUntil() normalises BOTH sides to local midnight and is DST-safe, so an
    // item due TODAY comes back 0 and is not overdue. Hand-rolling this
    // comparison has shipped the same off-by-one twice in this module (#2053,
    // then #2237 at a call site #2053 missed) and a third instance was caught in
    // review. A missing or unparseable date returns null, and `null < 0` is
    // false, so it is never overdue.
    isOverdue(value) {
      return daysUntil(value) < 0;
    },
    dueLabel(value) {
      const days = daysUntil(value);
      if (days === null) return '';
      if (days === 0) return 'today';
      if (days < 0) return `${-days}d late`;
      return `in ${days}d`;
    },
  },
};
</script>
